/**
 * Model categories API client — reads and admin-mutates the data-driven model
 * classification list.
 *
 * The category list replaces the legacy hardcoded tier options; it is served
 * from the backend so categories can be added/renamed/reordered without a UI
 * deploy. Admin mutations (create/update/reorder) back the category-admin panel.
 */

import { logger } from '@/utils/logger';

import { api } from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A selectable model category returned by GET /api/model-categories. */
export interface ModelCategory {
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

interface ModelCategoriesResponse {
  data: ModelCategory[];
  success: boolean;
  message: string;
}

interface ModelCategoryResponse {
  data: ModelCategory;
  success: boolean;
  message: string;
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

/**
 * API client for reading and admin-mutating the data-driven model category list.
 */
export const modelCategories = {
  /**
   * Fetch model categories.
   *
   * Defaults to active-only (the picker's view). Pass `includeInactive: true`
   * to also return deactivated rows — used by the admin panel, which must show
   * inactive categories so they can be re-enabled or reordered.
   *
   * Unwraps the standard `{ data }` success envelope, returning an empty array
   * when the payload is missing. Errors are logged and re-thrown so callers can
   * surface a load-failure state.
   *
   * @param options - Optional flags; `includeInactive` adds inactive rows.
   * @returns The list of model categories.
   * @throws Re-throws the underlying request error after logging.
   */
  async getAll(options?: { includeInactive?: boolean }): Promise<ModelCategory[]> {
    try {
      // Trailing slash required: the collection-root GET is registered at
      // `/api/model-categories/` (FastAPI router `@get("/")`). Without it the
      // request fails and the picker degrades to alphabetical raw-key groups.
      const url = options?.includeInactive
        ? '/api/model-categories/?include_inactive=true'
        : '/api/model-categories/';
      const response = await api.get<ModelCategoriesResponse>(url);
      return response.data.data || [];
    } catch (error) {
      logger.error('Failed to get model categories:', error);
      throw error;
    }
  },

  /**
   * Create a new model category.
   *
   * POSTs to the trailing-slash collection root (matching the read route) and
   * unwraps the created category from the `{ data }` envelope.
   *
   * @param input - The new category's `key` (stable identifier) and `label`.
   * @returns The created category.
   * @throws Re-throws the underlying request error after logging.
   */
  async create(input: { key: string; label: string }): Promise<ModelCategory> {
    try {
      const response = await api.post<ModelCategoryResponse>('/api/model-categories/', input);
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create model category:', error);
      throw error;
    }
  },

  /**
   * Partially update a model category (rename or toggle active).
   *
   * PATCHes the item route (no trailing slash) with only the provided fields;
   * the backend reads camelCase `label`/`isActive`. Unwraps the updated category.
   *
   * @param key - The category's stable identifier.
   * @param changes - The fields to change (`label` and/or `isActive`).
   * @returns The updated category.
   * @throws Re-throws the underlying request error after logging.
   */
  async update(
    key: string,
    changes: { label?: string; isActive?: boolean }
  ): Promise<ModelCategory> {
    try {
      const response = await api.patch<ModelCategoryResponse>(
        `/api/model-categories/${key}`,
        changes
      );
      return response.data.data;
    } catch (error) {
      logger.error(`Failed to update model category ${key}:`, error);
      throw error;
    }
  },

  /**
   * Reorder the full category list.
   *
   * POSTs the complete ordered list of keys to the reorder route (no trailing
   * slash); the backend rewrites `sortOrder` to match. Unwraps the reordered
   * categories, returning an empty array when the payload is missing.
   *
   * @param keys - The full list of category keys in the desired order.
   * @returns The reordered categories.
   * @throws Re-throws the underlying request error after logging.
   */
  async reorder(keys: string[]): Promise<ModelCategory[]> {
    try {
      const response = await api.post<ModelCategoriesResponse>('/api/model-categories/reorder', {
        keys,
      });
      return response.data.data || [];
    } catch (error) {
      logger.error('Failed to reorder model categories:', error);
      throw error;
    }
  },
};
